import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  LocalPharmacy,
  Add,
  Notifications,
  Inventory,
  ShoppingCart,
  DeliveryDining,
  TrendingUp,
  Person,
  ExitToApp,
  Security,
  Search,
  LocationOn,
  Phone,
  Share,
  Visibility,
  Message,
  Call
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../../services/orderService';
import { productService } from '../../services/productService';
import { pharmacyService } from '../../services/pharmacyService';
import { chatService } from '../../services/chatService';
import { locationService } from '../../services/locationService';
import AddProductModal from '../../components/AddProductModal';
import VerificationWizard from '../../components/VerificationWizard';
import SimpleDocumentUpload from '../../components/SimpleDocumentUpload';
import NotificationPanel from '../../components/NotificationPanel';
import ChatWindow from '../../components/ChatWindow';
import Chat from '@mui/icons-material/Chat';

const PharmacyDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableRiders, setAvailableRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ridersLastUpdated, setRidersLastUpdated] = useState(null);
  const [verificationProgress, setVerificationProgress] = useState(null);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState('');
  const [currentDocumentLabel, setCurrentDocumentLabel] = useState('');
  const [pharmacyProfile, setPharmacyProfile] = useState(null);
  const [assignOrderModalOpen, setAssignOrderModalOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);
  const [pharmacyLocation, setPharmacyLocation] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const { logout } = useAuth();
  const { notifications, socket } = useSocket();
  const navigate = useNavigate();

  // Load data on component mount
  useEffect(() => {
    loadData();
    
    // Set up periodic refresh for available riders (every 30 seconds)
    const ridersInterval = setInterval(() => {
      loadAvailableRiders();
    }, 30000);
    
    // Set up periodic refresh for conversations (every 10 seconds)
    const conversationsInterval = setInterval(() => {
      if (tabValue === 4) { // Only refresh when on Conversations tab
        loadConversations();
      }
    }, 10000);
    
    return () => {
      clearInterval(ridersInterval);
      clearInterval(conversationsInterval);
    };
  }, [tabValue]);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (socket) {
      // Listen for new conversations
      socket.on('new-conversation', (data) => {
        // New conversation received
        // Refresh conversations list
        loadConversations();
      });

      // Listen for new messages in existing conversations
      socket.on('new-message', (data) => {
        // New message received
        // Refresh conversations list to show updated last message
        loadConversations();
      });

      return () => {
        socket.off('new-conversation');
        socket.off('new-message');
      };
    }
  }, [socket]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get pharmacy location
      try {
        const locationResult = await locationService.getCurrentLocation();
        if (locationResult.success && locationResult.hasLocation) {
          setPharmacyLocation(locationResult.location);
          setLocationPermission('granted');
        } else {
          setLocationPermission('denied');
        }
      } catch (locationError) {
        setLocationPermission('denied');
      }

      const [ordersData, productsData, ridersData, profileData] = await Promise.all([
        orderService.getPharmacyOrders(),
        productService.getMyProducts(),
        pharmacyService.getAvailableRiders(),
        pharmacyService.getProfile()
      ]);
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setProducts(Array.isArray(productsData) ? productsData : []);
      setAvailableRiders(Array.isArray(ridersData) ? ridersData : []);
      setRidersLastUpdated(new Date());
      
      // Store pharmacy profile data
      setPharmacyProfile(profileData);
      
      // Set verification progress from profile data
      if (profileData && profileData.verificationProgress) {
        setVerificationProgress(profileData.verificationProgress);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRiders = async () => {
    try {
      // Get pharmacy location first
      const locationResult = await locationService.getCurrentLocation();
      if (locationResult.success && locationResult.hasLocation) {
        const ridersData = await locationService.getNearbyRiders(
          locationResult.location.latitude,
          locationResult.location.longitude,
          5 // 5km radius
        );
        setAvailableRiders(Array.isArray(ridersData.riders) ? ridersData.riders : []);
      } else {
        // Fallback to all riders if no location
        const ridersData = await locationService.getAllRidersWithLocation();
        setAvailableRiders(Array.isArray(ridersData.riders) ? ridersData.riders : []);
      }
      setRidersLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading available riders:', error);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await orderService.updateOrderStatus(orderId, status);
      await loadData(); // Reload data to get updated orders
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status');
    }
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      await orderService.confirmPayment(orderId);
      await loadData(); // Reload data to get updated orders
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError('Failed to confirm payment');
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Auto-load conversations when switching to Conversations tab
    if (newValue === 4) {
      loadConversations();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const loadConversations = async () => {
    try {
      setConversationsLoading(true);
      const data = await chatService.getPharmacyConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleOpenChat = async (conversation) => {
    try {
      setCurrentConversation(conversation);
      setCurrentCustomer({
        firstName: conversation.customerId?.firstName,
        lastName: conversation.customerId?.lastName,
        phone: conversation.customerId?.phone
      });
      setChatOpen(true);
    } catch (error) {
      console.error('Error opening chat:', error);
      setError('Failed to open chat');
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setCurrentConversation(null);
    setCurrentCustomer(null);
  };

  const handleAddProduct = () => {
    setAddProductModalOpen(true);
  };

  const handleProductAdded = () => {
    // Refresh products data after adding
    loadData();
  };

  const handleDocumentUpload = async (documentType, file) => {
    try {
      const response = await pharmacyService.uploadVerificationDocument(documentType, file);
      console.log('Document upload response:', response);
      
      // Reload data to get updated verification status
      await loadData();
      
      return response;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const openDocumentUpload = (documentType, documentLabel) => {
    setCurrentDocumentType(documentType);
    setCurrentDocumentLabel(documentLabel);
    setDocumentUploadModalOpen(true);
  };

  const handleAssignOrder = (rider) => {
    setSelectedRider(rider);
    setAssignOrderModalOpen(true);
  };

  const handleContactRider = (rider) => {
    console.log('Contact rider button clicked for:', rider);
    console.log('Rider phone number:', rider.phone);
    
    // Open phone dialer
    const phoneNumber = rider.phone;
    if (phoneNumber) {
      // Clean the phone number (remove all non-digits except +)
      const cleanPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
      console.log('Cleaned phone number:', cleanPhoneNumber);
      
      // Use tel: protocol to initiate a phone call
      const phoneUrl = `tel:${cleanPhoneNumber}`;
      console.log('Opening phone URL:', phoneUrl);
      
      // Use window.location.href directly to open phone dialer
      window.location.href = phoneUrl;
      console.log('Phone call initiated successfully');
    } else {
      console.log('No phone number not available for rider');
      alert('Phone number not available for this rider');
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'preparing': return 'primary';
      case 'ready': return 'success';
      case 'delivered': return 'success';
      default: return 'default';
    }
  };

  // Helper functions for analytics calculations
  const getCompletedOrders = () => {
    return orders.filter(order => 
      order.status === 'delivered' || 
      order.status === 'picked-up' ||
      order.status === 'assigned' ||
      order.status === 'ready' || 
      order.status === 'confirmed' || 
      order.status === 'preparing'
    );
  };

  const getAnalyticsOrders = () => {
    return orders.filter(order => 
      order.status === 'delivered' || 
      order.status === 'picked-up' ||
      order.status === 'assigned' ||
      order.status === 'ready' || 
      order.status === 'confirmed' || 
      order.status === 'preparing' ||
      order.status === 'cancelled'
    );
  };

  const getTotalRevenue = () => {
    return getCompletedOrders().reduce((sum, order) => sum + (order.totalAmount || 0), 0);
  };

  const getTotalOrdersCount = () => {
    return getAnalyticsOrders().length;
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <LocalPharmacy sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
            Dashboard
          </Typography>
          <IconButton 
            color="inherit"
            onClick={async () => {
              try {
                const shareData = {
                  title: 'PD App - Pharmacy Delivery',
                  text: 'Check out PD App - Your trusted pharmacy delivery service!',
                  url: window.location.origin
                };
                
                if (navigator.share) {
                  await navigator.share(shareData);
                } else {
                  // Fallback for browsers that don't support Web Share API
                  await navigator.clipboard.writeText(window.location.origin);
                  alert('App link copied to clipboard!');
                }
              } catch (error) {
                console.error('Error sharing:', error);
              }
            }}
          >
            <Share />
          </IconButton>
          <IconButton 
            color="inherit"
            onClick={() => setNotificationPanelOpen(true)}
          >
            <Badge badgeContent={notifications.length} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 }, px: { xs: 1, sm: 3 } }}>
        {/* Welcome Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Welcome back, {pharmacyProfile?.pharmacyName || 'Pharmacy'}!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Manage your pharmacy operations efficiently
          </Typography>
        </Box>


        {/* Stats Cards */}
        <Grid container spacing={{ xs: 0.5, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <ShoppingCart sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'primary.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {getTotalOrdersCount()}
                    </Typography>
                    <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: 'text.secondary' }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Total Orders
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Orders
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <TrendingUp sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'success.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      ₦{getTotalRevenue().toLocaleString()}
                    </Typography>
                    <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: 'text.secondary' }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Total Revenue
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Revenue
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Inventory sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'warning.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {products.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Products
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <DeliveryDining sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'secondary.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {availableRiders.length}
                    </Typography>
                    <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: 'text.secondary' }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Available Riders
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Riders
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 0.5, sm: 2 },
                  minHeight: { xs: 40, sm: 48 }
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Recent Orders
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Orders
                    </Box>
                    <Badge 
                      badgeContent={orders.length} 
                      color="primary"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Product Management
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Products
                    </Box>
                    <Badge 
                      badgeContent={products.length} 
                      color="secondary"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Available Riders
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Riders
                    </Box>
                    <Badge 
                      badgeContent={availableRiders.length} 
                      color="success"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab label="Analytics" />
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Conversations
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Chat
                    </Box>
                    <Badge 
                      badgeContent={conversations.length} 
                      color="error"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <Security sx={{ fontSize: { xs: 14, sm: 16 } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Verification
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Verify
                    </Box>
                    {verificationProgress && (
                      <Chip
                        label={`${verificationProgress.progress}%`}
                        size="small"
                        color={verificationProgress.progress === 100 ? 'success' : 'primary'}
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
                          height: { xs: 18, sm: 20 },
                          display: { xs: 'none', sm: 'flex' }
                        }}
                      />
                    )}
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Recent Orders
              </Typography>
              <Button variant="outlined" size="small">
                View All
              </Button>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : orders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No orders yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Orders will appear here when customers place them
                </Typography>
              </Box>
            ) : (
              <List>
                {orders.slice(0, 5).map((order, index) => (
                <React.Fragment key={order._id || order.id || index}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Person />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Order #{order.orderNumber || order._id?.slice(-8)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.customerId ? `${order.customerId.firstName} ${order.customerId.lastName}` : 'Unknown Customer'}
                          </Typography>
                        </Box>
                      }
                      secondary={`${order.items.length} items • ₦${order.totalAmount.toLocaleString()} • ${new Date(order.createdAt).toLocaleDateString()}`}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {order.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleUpdateOrderStatus(order._id, 'confirmed')}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleUpdateOrderStatus(order._id, 'cancelled')}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {order.status === 'confirmed' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleUpdateOrderStatus(order._id, 'preparing')}
                        >
                          Start Preparing
                        </Button>
                      )}
                      {order.status === 'preparing' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() => handleUpdateOrderStatus(order._id, 'ready')}
                        >
                          Ready for Pickup
                        </Button>
                      )}
                      {order.paymentStatus === 'pending' && order.status !== 'cancelled' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() => handleConfirmPayment(order._id)}
                        >
                          Confirm Payment
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOrderClick(order)}
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < orders.slice(0, 5).length - 1 && <Divider />}
                </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Product Management
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={handleAddProduct}>
                Add Product
              </Button>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No products yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add your first product to get started
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={handleAddProduct}>
                  Add Product
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {products.slice(0, 6).map((product) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product._id || product.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {product.category}
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          ₦{product.price?.nigeria?.toLocaleString() || product.price?.toLocaleString() || '0'}
                        </Typography>
                        <Chip 
                          label={product.isAvailable ? 'In Stock' : 'Out of Stock'} 
                          color={product.isAvailable ? 'success' : 'error'} 
                          size="small" 
                          sx={{ mt: 1 }} 
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Available Riders in Your Area
                </Typography>
                {ridersLastUpdated && (
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {ridersLastUpdated.toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={loadAvailableRiders}>
                  Refresh
                </Button>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={async () => {
                    try {
                      const result = await locationService.requestLocationPermission();
                      if (result.success) {
                        await locationService.updateLocation(
                          result.position.latitude,
                          result.position.longitude
                        );
                        setPharmacyLocation({
                          latitude: result.position.latitude,
                          longitude: result.position.longitude
                        });
                        setLocationPermission('granted');
                        loadAvailableRiders();
                      }
                    } catch (error) {
                      console.error('Location update error:', error);
                    }
                  }}
                  startIcon={<LocationOn />}
                >
                  Update Location
                </Button>
              </Box>
            </Box>
            
            {/* Location Status */}
            {locationPermission === 'denied' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Location access is required to show nearby riders. Please enable location permissions and update your location.
                </Typography>
              </Alert>
            )}
            
            {locationPermission === 'granted' && pharmacyLocation && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Showing riders within 5km of your location
                </Typography>
              </Alert>
            )}
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : availableRiders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DeliveryDining sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No riders available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Riders will appear here when they turn on their availability switch
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 2 }}
                  onClick={loadAvailableRiders}
                >
                  Check Again
                </Button>
              </Box>
            ) : (
              <List>
                {availableRiders.map((rider, index) => (
                <React.Fragment key={rider._id || rider.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <DeliveryDining />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${rider.firstName || rider.name} ${rider.lastName || ''}`}
                      secondary={
                        <Box component="div">
                          <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                            📞 {rider.phone}
                          </Box>
                          <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                            ⭐ Rating: {rider.rating || 'New Rider'} • 🚗 {rider.vehicleInfo?.type || 'Vehicle'} • 📦 {rider.totalDeliveries || 0} deliveries
                            {rider.distance && ` • 📍 ${rider.distance}km away`}
                          </Box>
                          {rider.currentLocation && (
                            <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                              📍 Last seen: {new Date(rider.currentLocation.lastUpdated).toLocaleTimeString()}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        size="small" 
                        color="success"
                        onClick={() => handleAssignOrder(rider)}
                      >
                        Assign Order
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<Phone />}
                        onClick={() => handleContactRider(rider)}
                      >
                        Contact
                      </Button>
                    </Box>
                  </ListItem>
                  {index < availableRiders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Analytics & Reports
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sales Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This week's performance
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="h4" color="primary.main">
                            {getTotalOrdersCount()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Orders
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="h4" color="success.main">
                            ₦{getTotalRevenue().toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Revenue
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="h4" color="info.main">
                            {orders.filter(order => order.status === 'delivered').length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Completed Orders
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="h4" color="warning.main">
                            {orders.filter(order => order.status === 'pending').length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pending Orders
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="h4" color="error.main">
                            {orders.filter(order => order.status === 'cancelled').length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cancelled Orders
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Products
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best selling items
                    </Typography>
                    {(() => {
                      // Calculate top products from completed orders only (excludes cancelled and pending)
                      const productSales = {};
                      getCompletedOrders().forEach(order => {
                        if (order.items && Array.isArray(order.items)) {
                          order.items.forEach(item => {
                            const productName = item.productId?.name || item.name || 'Unknown Product';
                            productSales[productName] = (productSales[productName] || 0) + (item.quantity || 1);
                          });
                        }
                      });
                      
                      const topProducts = Object.entries(productSales)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 3);
                      
                      return (
                        <Box sx={{ mt: 2 }}>
                          {topProducts.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No sales data available yet
                            </Typography>
                          ) : (
                            topProducts.map(([productName, quantity], index) => (
                              <Typography key={productName} variant="body2">
                                {index + 1}. {productName} - {quantity} sold
                              </Typography>
                            ))
                          )}
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Customer Conversations
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={loadConversations}
                disabled={conversationsLoading}
                startIcon={conversationsLoading ? <CircularProgress size={16} /> : null}
              >
                {conversationsLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>

            {loading || conversationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : conversations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No conversations yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start chatting with customers when they message you
                </Typography>
              </Box>
            ) : (
              <List>
                {conversations.map((conversation, index) => (
                  <React.Fragment key={conversation._id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {conversation.customerId?.firstName?.charAt(0) || 'C'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${conversation.customerId?.firstName || ''} ${conversation.customerId?.lastName || ''}`}
                        secondary={
                          <Box component="div">
                            <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                              {conversation.lastMessage || 'No messages yet'}
                            </Box>
                            <Box component="div" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {conversation.lastMessageAt 
                                ? new Date(conversation.lastMessageAt).toLocaleString()
                                : 'No messages yet'}
                            </Box>
                          </Box>
                        }
                      />
                      <Chip
                        label={conversation.status}
                        color={conversation.status === 'active' ? 'success' : 'default'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Chat />}
                        onClick={() => handleOpenChat(conversation)}
                      >
                        Open Chat
                      </Button>
                    </ListItem>
                    {index < conversations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Account Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete your account verification to start receiving orders from customers
            </Typography>
            
            <VerificationWizard
              verificationProgress={verificationProgress || {
                progress: 0,
                completedSteps: 0,
                totalSteps: 4,
                remainingSteps: [
                  'Complete your profile',
                  'Upload pharmacy license',
                  'Upload business registration',
                  'Upload tax certificate'
                ],
                status: 'pending',
                isVerified: false,
                steps: {
                  profileComplete: false,
                  licenseUploaded: false,
                  businessRegistrationUploaded: false,
                  taxCertificateUploaded: false
                }
              }}
              userType="pharmacy"
              onUploadDocument={(documentType) => {
                const documentLabels = {
                  'license': 'Pharmacy License',
                  'businessRegistration': 'Business Registration',
                  'taxCertificate': 'Tax Certificate'
                };
                openDocumentUpload(documentType, documentLabels[documentType]);
              }}
              onSubmitVerification={async () => {
                console.log('Pharmacy submit verification button clicked');
                try {
                  const result = await pharmacyService.submitForVerification();
                  console.log('Pharmacy verification submission result:', result);
                  
                  // Update verification progress immediately from the response
                  if (result && result.verificationProgress) {
                    console.log('Updating verification progress from submission response:', result.verificationProgress);
                    setVerificationProgress(result.verificationProgress);
                  }
                  
                  // Reload data to get updated verification status
                  await loadData();
                  console.log('Pharmacy data reloaded after verification submission');
                } catch (error) {
                  console.error('Error submitting pharmacy verification:', error);
                  // Show error message to user
                  setError('Failed to submit verification. Please try again.');
                }
              }}
            />
          </TabPanel>
        </Card>
      </Container>

      {/* Chat Window */}
      {chatOpen && currentConversation && currentCustomer && (
        <ChatWindow
          conversation={currentConversation}
          pharmacy={{
            pharmacyName: pharmacyProfile?.pharmacyName || 'Pharmacy',
            contactInfo: pharmacyProfile?.contactInfo
          }}
          onClose={handleCloseChat}
          onOrderPlaced={() => {
            handleCloseChat();
            loadData();
          }}
        />
      )}

      <AddProductModal
        open={addProductModalOpen}
        onClose={() => setAddProductModalOpen(false)}
        onSuccess={handleProductAdded}
      />

      <SimpleDocumentUpload
        open={documentUploadModalOpen}
        onClose={() => setDocumentUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
        documentType={currentDocumentType}
        documentLabel={currentDocumentLabel}
        verificationProgress={verificationProgress}
      />

      {/* Assign Order Modal */}
      <Dialog open={assignOrderModalOpen} onClose={() => setAssignOrderModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Assign Order to {selectedRider?.firstName} {selectedRider?.lastName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select an order to assign to this rider:
          </Typography>
          
          {/* Search functionality */}
          <TextField
            fullWidth
            placeholder="Search orders by ID or customer name..."
            value={orderSearchQuery}
            onChange={(e) => setOrderSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          
          {(() => {
            // Filter orders: only unassigned orders (no riderId) and match search query
            const unassignedOrders = orders.filter(order => 
              !order.riderId && 
              (order.status === 'pending' || order.status === 'confirmed' || order.status === 'ready')
            );
            
            const filteredOrders = unassignedOrders.filter(order => {
              if (!orderSearchQuery) return true;
              const searchLower = orderSearchQuery.toLowerCase();
              const orderId = order.orderNumber || order._id?.slice(-8) || '';
              const customerName = order.customerId?.firstName + ' ' + order.customerId?.lastName || '';
              return orderId.toLowerCase().includes(searchLower) || 
                     customerName.toLowerCase().includes(searchLower);
            });
            
            return filteredOrders.length === 0 ? (
              <Alert severity="info">
                {unassignedOrders.length === 0 
                  ? "No unassigned orders available to assign." 
                  : "No orders match your search criteria."
                }
              </Alert>
            ) : (
              <List>
                {filteredOrders.map((order) => (
                <ListItem key={order._id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, mb: 1 }}>
                  <ListItemText
                    primary={`Order #${order.orderNumber || order._id.slice(-6)}`}
                    secondary={
                      <Box component="div">
                        <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                          Customer: {order.customerId?.firstName} {order.customerId?.lastName}
                        </Box>
                        <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                          Items: {order.items?.length || 0} items
                        </Box>
                        <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                          Total: ₦{order.totalAmount?.toLocaleString() || '0'}
                        </Box>
                        <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          Status: {order.status}
                        </Box>
                      </Box>
                    }
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      try {
                        const result = await orderService.assignOrderToRider(order._id, selectedRider._id);
                        console.log('Order assigned successfully:', result);
                        alert(`Order assigned to ${selectedRider.firstName} ${selectedRider.lastName} successfully!`);
                        setAssignOrderModalOpen(false);
                        // Reload data to show updated order status
                        loadData();
                      } catch (error) {
                        console.error('Error assigning order:', error);
                        alert(`Failed to assign order: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Assign
                  </Button>
                </ListItem>
                ))}
              </List>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssignOrderModalOpen(false);
            setOrderSearchQuery('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Panel */}
      <NotificationPanel 
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />

      {/* Order Details Dialog */}
      <Dialog 
        open={orderDetailsOpen} 
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Order Details
          {selectedOrder && (
            <Typography variant="body2" color="text.secondary">
              Order #{selectedOrder.orderNumber || selectedOrder._id?.slice(-8)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              {/* Order Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={selectedOrder.status?.replace('-', ' ')}
                  color={getStatusColor(selectedOrder.status)}
                  sx={{ textTransform: 'capitalize', mr: 1 }}
                />
                <Chip
                  label={`Payment: ${selectedOrder.paymentStatus || 'pending'}`}
                  color={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>

              {/* Customer Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedOrder.customerId?.firstName} {selectedOrder.customerId?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                  </Box>
                </Box>
                
                {selectedOrder.customerId?.phone && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 7 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Call />}
                      onClick={() => window.open(`tel:${selectedOrder.customerId.phone}`)}
                      sx={{ textTransform: 'none' }}
                    >
                      Call
                    </Button>

                  </Box>
                )}
              </Box>

              {/* Rider Information */}
              {selectedOrder.riderId && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Assigned Rider
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <DeliveryDining />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedOrder.riderId?.firstName} {selectedOrder.riderId?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivery Rider
                      </Typography>
                    </Box>
                  </Box>
                  
                  {selectedOrder.riderId?.phone && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 7 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Call />}
                        onClick={() => window.open(`tel:${selectedOrder.riderId.phone}`)}
                        sx={{ textTransform: 'none' }}
                      >
                        Call Rider
                      </Button>

                    </Box>
                  )}
                </Box>
              )}

              {/* Delivery Address */}
              {selectedOrder.deliveryAddress && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Delivery Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.deliveryAddress.street}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.state}
                  </Typography>
                </Box>
              )}

              {/* Order Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Items ({selectedOrder.items?.length || 0})
                </Typography>
                {selectedOrder.items && Array.isArray(selectedOrder.items) ? (
                  <List>
                    {selectedOrder.items.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.productId?.name || item.name || 'Unknown Product'}
                          secondary={
                            <Box>
                              <Typography variant="body2" component="div">
                                Quantity: {item.quantity}
                              </Typography>
                              <Typography variant="body2" component="div">
                                Unit Price: ₦{(item.price || item.unitPrice)?.toLocaleString()}
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                Total: ₦{((item.price || item.unitPrice) * item.quantity)?.toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : null}
              </Box>

              {/* Order Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">₦{(selectedOrder.totalAmount || selectedOrder.amount)?.toLocaleString()}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">₦{(selectedOrder.totalAmount || selectedOrder.amount)?.toLocaleString()}</Typography>
                </Box>
              </Box>

              {/* Payment Receipt */}
              {selectedOrder.paymentReceipt && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Payment Receipt
                  </Typography>
                  <Box
                    component="img"
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${selectedOrder.paymentReceipt}`}
                    alt="Payment Receipt"
                    sx={{
                      width: '100%',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => {
                      window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${selectedOrder.paymentReceipt}`, '_blank');
                    }}
                    onError={(e) => {
                      console.error('Failed to load image:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PharmacyDashboard;
